/* eslint-disable comma-spacing */
import React from 'react';
import _ from 'lodash';
import {
	NO_SHOPS, ERROR_POSITIVE_VALUE,
	ERROR_DATE_CONFLICT, ERROR_TOO_SHORT_PERIOD,
	ERROR_CREATE_CAMPAIGN_MARKETING,
	ERROR_PRODUCT, ERROR_PRODUCT_CONFLICT, INVALID_START_DATE,
	INVALID_DATA, NO_PURCHASED_PRODUCT, INVALID_VALUE, NO_SELECTED_PRODUCT, SUCCESS_UPDATE_TEXT, ERROR_DUPLICATE_OFFER,
	ERROR_LIMIT_REQUIRED, ERROR_CAMPAIGN_IMAGE_NOT_PUBLIC
} from '../../constants';
import { createCampaignMarketing, updateMarketingCampaign, uploadImage } from '../../apis/backend';
import {
	CampaignItem, MARKETING_CAMPAIGN_IS_ABOUT_TO_START, CampaignType,
	MARKETING_CAMPAIGN_DATE_CONFLICT, MARKETING_CAMPAIGN_PRODUCT_CONFLICT, MARKETING_CAMPAIGN_IS_TOO_SHORT,
	MARKETING_CAMPAIGN_STATUS, TYPES
} from '../../constants/marketingCampaign'
import { showNotification } from '../../components/Notification/Notification.view';
import { getAllCampaigns } from './MarketingCampaigns.services';
import { combineDateAndTime, formatDateTime } from '../../utils/tools'
import { getCroppedImg } from '../../utils/image'
import moment, { Moment } from 'moment';
import { ALL_ORDER_TYPES } from '../../constants/dataConfig';
import {
	assertPublicCampaignImageUrl,
	CAMPAIGN_IMAGE_STORAGE_FOLDER,
	normalizeCampaignImageUrl,
} from './CampaignModalSkeleton/CampaignModalSkeleton.imageUrl';

export interface Product {
	ref: string;
	name: string;
	key: string;
	price: number
}
export interface MenuCategory {
	key: string;
	name: string;
	items: {
		[itemKey: string]: {
			key: string;
			name: string;
			price: number;
			position: number;
		};
	};
}

export interface Menu {
	[shopId: string]: {
		sections: {
			[sectionId: string]: {
				key?: string,
				name: string,
				position: number,
				isPrivate?: boolean,
				products?: {
					[productId: string]: Product;
				};
			};
		};
		categories?: {
			[categoryKey: string]: MenuCategory;
		};
	};
}
export interface Section {
	id: string;
	name: string;
	products: Product[];
	position: number;
}

export const hasPaidPrice = (item: { price?: unknown }): boolean => {
	const price = Number(item?.price);
	return Number.isFinite(price) && price > 0;
};

// eslint-disable-next-line comma-spacing
export const toggleArrayItem = <T,>(
	currentArray: T[],
	item: T,
	options?: { minLength?: number; isEqual?: (a: T, b: T) => boolean }
): T[] => {
	const array = currentArray || [];
	const isEqual = options?.isEqual ?? ((a, b) => a === b);
	const index = array.findIndex((x) => isEqual(x, item));
	const isSelected = index !== -1;

	if (isSelected) {
		const minLength = options?.minLength ?? 0;
		if (array.length <= minLength) return array;
		return array.filter((_, i) => i !== index);
	}

	return [...array, item];
};

export const getAvailableOrderTypes = (
	shopIds: string[],
	shops: Record<string, { configuration?: { orderTypes?: string[] } }>,
	generalConfig?: { orderTypes?: string[] }
): string[] => {
	if (!shopIds || shopIds.length === 0) {
		return generalConfig?.orderTypes || ALL_ORDER_TYPES;
	}
	const orderTypesSets = shopIds
		.map(id => shops[id]?.configuration?.orderTypes)
		.filter(Boolean);
	if (orderTypesSets.length === 0) {
		return generalConfig?.orderTypes || ALL_ORDER_TYPES;
	}
	return _.uniq(...orderTypesSets);
};

export const handleRestaurantOpenModal = (
	campaign: CampaignItem,
	setOpenRestaurantModal: React.Dispatch<React.SetStateAction<any>>,
	setSelectedCampaign: React.Dispatch<React.SetStateAction<any>>,
	setSelectedShops: React.Dispatch<React.SetStateAction<any>>,
) => {
	setOpenRestaurantModal(true);
	setSelectedShops([]);
	setSelectedCampaign(campaign);
};

export const handleOpenOneShopModal = (campaign: CampaignItem, setSelectedCampaign: React.Dispatch<React.SetStateAction<any>>, shopId: number, setSelectedShops: React.Dispatch<React.SetStateAction<any>>, setOpenModal: React.Dispatch<React.SetStateAction<any>>) => {
	setSelectedCampaign(campaign);
	setSelectedShops([shopId]);
	setOpenModal(true);
}



export const getCommonProductsBySection = (shopIds: string[], menus: Menu): Section[] => {
	if (!menus || !shopIds || !shopIds.length) {
		return [];
	}

	const shopSections = shopIds.map(shopId => {
		const menu = menus[shopId];
		if (!menu || !menu.sections) return [];

		return Object.entries(menu.sections).map(([sectionId, section]) => ({
			sectionId,
			sectionName: section?.name || `Section ${sectionId}`,
			position: section?.position ?? 0,
			products: section?.products
				? Object.values(section.products).filter(hasPaidPrice)
				: []
		}));
	});

	if (shopSections.some(sections => sections.length === 0)) {
		return [];
	}

	const commonSectionIds = _.intersection(
		...shopSections.map(sections => sections.map(s => s.sectionId))
	);

	const result = commonSectionIds
		.map(sectionId => {
			const baseSection = shopSections[0].find(s => s.sectionId === sectionId);
			const sectionName = baseSection?.sectionName || `Section ${sectionId}`;
			const position = baseSection?.position ?? 0;

			const allSectionProducts = shopSections.map(sections => {
				const section = sections.find(s => s.sectionId === sectionId);
				return section ? section.products : [];
			});

			const commonProducts = allSectionProducts.reduce((common, products) => {
				if (!common?.length) return products;
				return common.filter(product =>
					products.some(p => p.key === product.key)
				);
			}, [] as Product[]);

			return {
				id: sectionId,
				name: sectionName,
				position,
				products: _.uniqBy(commonProducts, 'key')
			};
		})
		.filter(section => section.products.length > 0);

	return _.sortBy(result, 'position');
};


export const handleRestaurantsValidate = (selectedShopIds: string[], setSelectedShops, setOpenModal, openErrorModal, setOpenRestaurantModal) => {
	if (_.size(selectedShopIds) === 0 || !selectedShopIds) { openErrorModal(NO_SHOPS); return; }
	setSelectedShops(selectedShopIds);
	setOpenRestaurantModal(false)
	setOpenModal(true);


};


export const handlePromoValidate = async (
	formValues: any,
	selectedShops: string[],
	setOpenModal: React.Dispatch<React.SetStateAction<any>>,
	setOpenRestaurantModal: React.Dispatch<React.SetStateAction<any>>,
	openErrorModal: (message: string) => void,
	setIsLoading: React.Dispatch<React.SetStateAction<any>>,
	withoutProductSelection: boolean,
	isUpdate?: boolean,
	setMarketingCampaigns?: any,
	isAllShops?: boolean,
	shopId?: string,
	campaignCreatedBy?: string | null,
) => {
	setIsLoading(true)
	const minValidDate = (date) => formatDateTime(date.clone().add(15, 'minutes'));
	const formattedStartDate = combineDateAndTime(formValues.startDate, formValues.startTime);
	const formattedEndDate = formValues.isPermanent
		? null
		: combineDateAndTime(formValues.endDate, formValues.endTime);
	const validateFormData = (data: any) => {
		const { startDate, startTime, endTime, endDate } = formValues;
		const startMoment = moment(startDate);
		const now = moment();
		const isStartValid = isDateValid(startMoment, startTime, now);
		if (!isStartValid) {
			return {
				valid: false,
				error: INVALID_START_DATE(minValidDate(now))
			};
		}

		if (!formValues.isPermanent) {
			const isEndValid = isDateValid(moment(endDate), endTime, startMoment);
			if (!isEndValid) {
				return {
					valid: false,
					error: ERROR_TOO_SHORT_PERIOD(minValidDate(moment(formattedStartDate)))
				};
			}
		}
		if (CAMPAIGNS_WITH_OWN_CONFIG.includes(formValues.type)) {
			return { valid: true, error: '' };
		}

		const invalidValues = [
			data.config.discount?.value,
			data.config.threshold,
		];

		if (invalidValues.some(v => v === null || v <= 0)) {
			return { valid: false, error: ERROR_POSITIVE_VALUE };
		}
		if (data.config.limit === null || data.config.limit <= 0) {
			return { valid: false, error: ERROR_LIMIT_REQUIRED };
		}
		if (data.config.discount?.type === TYPES.CASH && data.config.discount?.value && data.config.threshold &&
			Number(data.config.discount.value) >= Number(data.config.threshold)) {
			return { valid: false, error: INVALID_DATA };
		}

		if (data.config.discount?.type === TYPES.PERCENTAGE && Number(data.config.discount.value) > 100) {
			return { valid: false, error: INVALID_VALUE };
		}
		if (!withoutProductSelection && (!data.config.products || _.size(data.config.products) === 0)) {
			return { valid: false, error: NO_SELECTED_PRODUCT };
		}
		if (formValues.type === CampaignType.PRODUCT_DISCOUNT_AFTER_THRESHOLD && (!formValues.limit || Number(formValues.limit) <= 0)) {
			return { valid: false, error: ERROR_LIMIT_REQUIRED };
		}
		return { valid: true, error: '' };
	};

	try {

		// N achetés M offerts: persist the chosen name/image inside config (campaignName + imageUrl).
		// A freshly picked image is a base64 data-URI and must be uploaded to obtain a link first.
		if (
			formValues.campaignName !== undefined ||
			formValues.campaignImage !== undefined
		) {
			let imageUrl = formValues.campaignImage || null;
			const imageConfig = formValues.campaignImageConfig;
			const shouldProcessImageConfig =
				imageConfig &&
				(!formValues.campaignImage || imageConfig.hasUserEdited);

			if (shouldProcessImageConfig) {
				imageUrl = await getCroppedImg(
					imageConfig.imageUrl,
					imageConfig.croppedAreaPixels,
				);
			}

			if (imageUrl && String(imageUrl).startsWith('data:')) {
				imageUrl = await uploadImage(imageUrl, {
					folder: CAMPAIGN_IMAGE_STORAGE_FOLDER,
				});
			}

			if (imageUrl) {
				const normalized = normalizeCampaignImageUrl(imageUrl);
				if (normalized.error || !normalized.url) {
					setIsLoading(false);
					return openErrorModal(ERROR_CAMPAIGN_IMAGE_NOT_PUBLIC);
				}
				imageUrl = normalized.url;
				try {
					await assertPublicCampaignImageUrl(imageUrl);
				} catch {
					setIsLoading(false);
					return openErrorModal(ERROR_CAMPAIGN_IMAGE_NOT_PUBLIC);
				}
			} else {
				imageUrl = null;
			}

			formValues.config = {
				...(formValues.config || {}),
				campaignName: formValues.campaignName,
				imageUrl,
			};
		}

		const firebaseData = setFormValues(
			formValues,
			formattedStartDate,
			formattedEndDate,
			selectedShops,
			withoutProductSelection,
			isUpdate,
			isAllShops,
			shopId
		);

		const validation = validateFormData(firebaseData);
		if (!validation.valid) {
			return openErrorModal(validation.error);
		}
		if (isUpdate) {
			const updateShopId = campaignCreatedBy || null;
			const fetchShopId = !isAllShops && shopId ? shopId : null;
			await updateMarketingCampaign(formValues?.marketingCampaignKey, firebaseData, updateShopId)
			await getAllCampaigns(setMarketingCampaigns, [MARKETING_CAMPAIGN_STATUS.PENDING], fetchShopId)
		}
		else await createCampaignMarketing(firebaseData)
		setOpenModal(false);
		setOpenRestaurantModal(false);
		setIsLoading(false)
		showNotification(
			'light',
			'ltr',
			'primary',
			isUpdate ? SUCCESS_UPDATE_TEXT : 'Création réussie',
			isUpdate ? 'La campagne promotionnelle a bien été modifiée' : 'La campagne promotionnelle a bien été créée'
		);

	} catch (error: any) {
		const errorName = error.response?.data?.errorName;
		const errorMessages = {
			[MARKETING_CAMPAIGN_DATE_CONFLICT]: ERROR_DATE_CONFLICT,
			[MARKETING_CAMPAIGN_IS_TOO_SHORT]: ERROR_TOO_SHORT_PERIOD(minValidDate(moment(formattedStartDate))),
			[MARKETING_CAMPAIGN_IS_ABOUT_TO_START]: INVALID_START_DATE(minValidDate(moment())),
			[MARKETING_CAMPAIGN_PRODUCT_CONFLICT]: ERROR_PRODUCT_CONFLICT,
		};
		const errorMessage = errorMessages[errorName] || ERROR_CREATE_CAMPAIGN_MARKETING;
		setIsLoading(false)
		openErrorModal(errorMessage);
	}
	finally {
		setIsLoading(false);
	}
};

const CAMPAIGNS_WITH_OWN_CONFIG = [
	CampaignType.BUY_N_GET_M_CHEAPEST_AT_DISCOUNT,
] as string[]

const setFormValues = (
	formValues: any,
	formattedStartDate: string,
	formattedEndDate: string | null,
	selectedShops: string[],
	withoutProductSelection: boolean,
	isUpdate?: boolean,
	isAllShops?: boolean,
	shopId?: string
) => {
	if (CAMPAIGNS_WITH_OWN_CONFIG.includes(formValues.type)) {
		const data: any = {
			config: { ...formValues.config },
			dateStart: formattedStartDate,
			orderTypes:
				formValues.orderTypes?.length > 0
					? formValues.orderTypes
					: ALL_ORDER_TYPES,
		}
		if (!formValues.isPermanent) {
			data.dateEnd = formattedEndDate
		}
		if (!isUpdate) {
			data.type = formValues.type
			data.shopIds = selectedShops
			data.createdBy = !isAllShops && shopId ? shopId : null
		}
		if (formValues.hasLimit && formValues.limit) {
			data.config.limit = Number(formValues.limit)
		}
		return data
	}

	const firebaseData: {
		config: {
			threshold?: number | null;
			discount?: { type: string; value: number | null };
			limit?: number | null;
			products?: any;
		};
		dateStart: string;
		dateEnd?: string;
		type?: string;
		shopIds?: string[];
		orderTypes?: string[];
		createdBy?: string | null;
	} = {
		config: {},
		dateStart: formattedStartDate,
	};
	if (!formValues.isPermanent) {
		firebaseData.dateEnd = formattedEndDate as string;
	}
	if (!isUpdate) {
		firebaseData.type = formValues.type
		firebaseData.shopIds = selectedShops
		firebaseData.createdBy = !isAllShops && shopId ? shopId : null
	}
	if (
		(withoutProductSelection && formValues.type !== CampaignType.FREE_DELIVERY_AFTER_THRESHOLD) ||
		(!withoutProductSelection && (formValues.type === CampaignType.PRODUCT_DISCOUNT_AFTER_THRESHOLD || formValues.type === CampaignType.PRODUCT_DISCOUNT))
	) {
		firebaseData.config.discount = {
			type: formValues.valueType,
			value: formValues.discount ? Number(formValues.discount) : null,
		};
	}


	if (withoutProductSelection || (!withoutProductSelection && formValues.type === CampaignType.PRODUCT_DISCOUNT_AFTER_THRESHOLD)) {
		firebaseData.config.threshold = formValues.threshold ? Number(formValues.threshold) : null;
	}

	if (formValues?.hasLimit && !withoutProductSelection) {
		firebaseData.config.limit = formValues.limit ? Number(formValues.limit) : null;
	}


	if (!withoutProductSelection) {
		firebaseData.config.products = formValues.products.map(product => {
			const { productOffredName, productName, ...rest } = product;
			return rest;
		});
	}

	firebaseData.orderTypes =
		formValues.orderTypes?.length > 0
			? formValues.orderTypes
			: ALL_ORDER_TYPES;

	return firebaseData;
};



export const handleProductToggle = (
	product,
	sectionKey,
	setPurchasedProducts,
	setFormValues,
	withAddButton
) => {
	const productItem = {
		productKey: product.key,
		productOfferedKey: product.key,
		productName: product.name,
		productSectionKey: sectionKey,
		productOfferedSectionKey: sectionKey,
		productOfferedName: product.name,
	};

	setPurchasedProducts(prev => toggleArrayItem(prev, productItem, {
		isEqual: (a, b) => a.productKey === b.productKey
	}));

	if (!withAddButton) {
		const formProductItem = { productKey: product.key, productSectionKey: sectionKey };
		setFormValues(prev => ({
			...prev,
			products: toggleArrayItem(prev.products || [], formProductItem, {
				isEqual: (a, b) => a.productKey === b.productKey
			})
		}));
	}
};


export const handleDateChange = (date, field, setFormValues) => {
	setFormValues(prev => ({
		...prev,
		[field]: date,
	}));
};



export const handleDelete = (index, setters, setFormValues) => {
	if (!setters || !Array.isArray(setters)) return;

	setters.forEach(setter => {
		if (typeof setter === 'function') {
			setter(prev => Array.isArray(prev) ? prev.filter((_, i) => i !== index) : prev);
		}
	});

	if (setFormValues && typeof setFormValues === 'function') {
		setFormValues(prev => ({
			...prev,
			products: Array.isArray(prev?.products) ? prev.products.filter((_, i) => i !== index) : []
		}));
	}
};
export const handlePurchasedProductToggle = (
	product,
	sectionKey,
	setOfferedProducts
) => {
	setOfferedProducts(prev => {
		if (prev.some(item => item.productKey === product.key)) {
			return prev.filter(item => item.productKey !== product.key);
		}
		return [
			{
				productKey: product.key,
				productSectionKey: sectionKey,
				productName: product.name,
				productOfferedKey: null,
				productOfferedSectionKey: null,
				productOfferedName: null
			}
		];
	});
};

export const handleOfferedProductToggle = (
	product,
	sectionKey,
	setOfferedProducts,
	offeredProducts,
	openErrorModal
) => {
	if (!offeredProducts || !offeredProducts.some(item => item.productKey)) {
		openErrorModal(NO_PURCHASED_PRODUCT);
		return;
	}

	setOfferedProducts(prev => {
		if (prev.some(item => item.productOfferedKey === product.key)) {
			return prev.map(item =>
				item.productOfferedKey === product.key
					? {
						...item,
						productOfferedKey: null,
						productOfferedSectionKey: null,
						productOfferedName: null
					}
					: item
			);
		}
		return prev.map(item => ({
			...item,
			productOfferedKey: product.key,
			productOfferedSectionKey: sectionKey,
			productOfferedName: product.name
		}));
	});
};


export const handleAddOfferedProducts = (
	offeredProducts,
	setSelectedProducts,
	setFormValues,
	openErrorModal,
	setOfferedProducts,
	setExpandedOffered,
	setExpandedPurchased
) => {
	const isValid = offeredProducts.some(
		item => item.productKey && item.productOfferedKey
	);

	if (!isValid) {
		openErrorModal(ERROR_PRODUCT);
		return;
	}

	const newOffer = offeredProducts.find(item => item.productKey && item.productOfferedKey);
	const offerKey = `${newOffer.productKey}-${newOffer.productOfferedKey}`;

	setSelectedProducts(prev => {
		const existingKeys = new Set(prev.map(p => `${p.productKey}-${p.productOfferedKey}`));

		if (existingKeys.has(offerKey)) {
			openErrorModal(ERROR_DUPLICATE_OFFER);
			return prev;
		}

		return [...prev, newOffer];
	});

	setFormValues(prev => {
		const existingProducts = prev.products || [];
		const existingKeys = new Set(existingProducts.map(p => `${p.productKey}-${p.productOfferedKey}`));

		if (existingKeys.has(offerKey)) {
			return prev;
		}

		const { productName, productOfferedName, ...rest } = newOffer;
		return {
			...prev,
			products: [...existingProducts, rest]
		};
	});

	setOfferedProducts([]);
	setExpandedOffered(false);
	setExpandedPurchased(true);
};

export const handleAddProducts = (purchasedProducts, setSelectedProducts, setFormValues, setDisabledPurshasedProducts) => {
	setSelectedProducts(prev => {
		const existingKeys = new Set(prev.map(p => p.productKey));
		const newProducts = purchasedProducts.filter(p => !existingKeys.has(p.productKey));
		return [...prev, ...newProducts];
	});

	setFormValues(prev => {
		const existingProducts = prev.products || [];
		const existingKeys = new Set(existingProducts.map(p => p.productKey));
		const newProducts = purchasedProducts
			.filter(p => !existingKeys.has(p.productKey))
			.map(({ productName, productOfferedName, ...rest }) => rest);

		return { ...prev, 'products': [...existingProducts, ...newProducts] };
	});
	setDisabledPurshasedProducts(purchasedProducts)
};

const isDateValid = (date: Moment, time: string, startDate: Moment): boolean => {
	const [hours, minutes] = time.split(':').map(Number);
	const selectedDate = date.hours(hours).minutes(minutes).seconds(0).milliseconds(0);
	const minValidDate = startDate.clone().add(15, 'minutes');

	return selectedDate.isSameOrAfter(minValidDate);
};
